/**
 * ConfermaTransizioneModal — conferma leggera ad OGNI passaggio di stato.
 * Mostra il firmatario attivo (già autenticato col PIN nel turno) + il picker "Eseguito da"
 * (esecutore reale di QUESTA operazione, default = firmatario). NIENTE PIN qui: la firma col PIN
 * è avvenuta all'ingresso turno; qui si conferma soltanto (basso attrito). "Cambia firmatario"
 * riapre la firma completa. Conferma → esegue l'azione con firmato_da + eseguito_da.
 */
import { useEffect, useState } from 'react';
import { X, UserCheck, Users, ChevronRight } from 'lucide-react';

interface Esec { id: string | number; displayName: string; soloDelega?: boolean }

interface Props {
  isOpen: boolean;
  descrizione?: string;
  firmatario: string;
  firmatarioId: string | number;
  eseguitoDaAttuale: string;
  onConferma: (eseguitoDa: string) => void;
  onAnnulla: () => void;
  onCambiaFirmatario: () => void;
}

const auth = () => { try { const t = localStorage.getItem('accessToken'); return t ? { Authorization: `Bearer ${t}` } : {}; } catch { return {}; } };

export default function ConfermaTransizioneModal({ isOpen, descrizione, firmatario, firmatarioId, eseguitoDaAttuale, onConferma, onAnnulla, onCambiaFirmatario }: Props) {
  const [esecutori, setEsecutori] = useState<Esec[]>([]);
  const [eseguitoDaId, setEseguitoDaId] = useState<string>('self');

  useEffect(() => {
    if (!isOpen) return;
    // Default: se l'eseguito attuale ≠ firmatario, prova a preselezionarlo; altrimenti "self".
    fetch('/api/mobile-operators/esecutori', { headers: { ...auth() } })
      .then(r => r.json()).then(d => {
        const list: Esec[] = d?.esecutori || [];
        setEsecutori(list);
        const match = list.find(e => e.displayName === eseguitoDaAttuale && e.displayName !== firmatario);
        setEseguitoDaId(match ? String(match.id) : 'self');
      }).catch(() => setEsecutori([]));
  }, [isOpen, eseguitoDaAttuale, firmatario]);

  if (!isOpen) return null;

  const conferma = () => {
    const nome = eseguitoDaId === 'self' ? firmatario : (esecutori.find(e => String(e.id) === eseguitoDaId)?.displayName || firmatario);
    onConferma(nome);
  };

  return (
    <div className="fixed inset-0 z-[420] bg-black/70 flex items-center justify-center p-4" onClick={onAnnulla}>
      <div onClick={e => e.stopPropagation()} className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-700">
          <div className="flex items-center gap-2"><UserCheck className="w-5 h-5 text-emerald-400" /><h3 className="text-white font-semibold">Conferma operazione</h3></div>
          <button onClick={onAnnulla} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          {descrizione && <p className="text-sm text-slate-300 mb-3">{descrizione}</p>}

          <div className="flex items-center justify-between mb-3 p-2.5 rounded-lg bg-slate-800 border border-slate-700">
            <div>
              <div className="text-[10px] text-slate-400 uppercase">Firmato da</div>
              <div className="text-sm text-emerald-300 font-semibold">{firmatario}</div>
            </div>
            <button onClick={onCambiaFirmatario} className="text-xs text-slate-400 hover:text-white underline">Cambia</button>
          </div>

          <label className="flex items-center gap-1.5 text-xs text-slate-400 mb-1"><Users className="w-3.5 h-3.5" /> Eseguito da (questa operazione)</label>
          <select value={eseguitoDaId} onChange={e => setEseguitoDaId(e.target.value)}
            className="w-full mb-4 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm text-white">
            <option value="self">Se stesso ({firmatario})</option>
            {esecutori.filter(e => String(e.id) !== String(firmatarioId)).map(e => (
              <option key={e.id} value={String(e.id)}>{e.displayName}{e.soloDelega ? ' — senza credenziali' : ''}</option>
            ))}
          </select>

          <div className="flex gap-2">
            <button onClick={onAnnulla} className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm">Annulla</button>
            <button onClick={conferma} className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-semibold flex items-center justify-center gap-1">
              Conferma <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
